import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Shield, 
  Plus,
  Download,
  Search,
  Building2,
  User,
  Calendar,
  Loader2,
  MoreVertical,
  RotateCcw,
  Trash2,
  Pencil,
  CheckCircle,
  Clock,
  Banknote,
  PenLine,
  FileText,
  Plus as PlusIcon,
  X,
  ListChecks,
  ArrowDownToLine
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { securityDepositsAPI, propertiesAPI, clientsAPI, signaturesAPI, leaseEndInvoicesAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { SignaturePad } from '../components/SignaturePad';

const SecurityDeposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRestituteModal, setShowRestituteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signDeposit, setSignDeposit] = useState(null);
  const [signatures, setSignatures] = useState({});
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [editForm, setEditForm] = useState({
    property_id: '',
    tenant_id: '',
    amount: '',
    currency: 'XOF',
    payment_method: 'especes',
    deposit_date: '',
    months_covered: '2',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    property_id: '',
    tenant_id: '',
    amount: '',
    currency: 'XOF',
    payment_method: 'especes',
    deposit_date: new Date().toISOString().split('T')[0],
    months_covered: '2',
    notes: ''
  });
  
  // Restitution form
  const [restituteData, setRestituteData] = useState({
    amount: '',
    notes: ''
  });

  const _newItem = () => ({ _id: Math.random().toString(36).slice(2), description: '', category: 'reparation', amount: '' });

  // Lease-end invoicing state
  const [showLeaseEndModal, setShowLeaseEndModal] = useState(false);
  const [leaseEndDeposit, setLeaseEndDeposit] = useState(null);
  const [leaseEndForm, setLeaseEndForm] = useState({
    departure_date: new Date().toISOString().split('T')[0],
    notes: '',
    items: [_newItem()],
  });
  const [showLeaseEndListModal, setShowLeaseEndListModal] = useState(false);
  const [leaseEndInvoicesForDeposit, setLeaseEndInvoicesForDeposit] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const LEASE_END_CATEGORIES = [
    { value: 'reparation', label: 'Réparation' },
    { value: 'charges_impayees', label: 'Charges impayées' },
    { value: 'nettoyage', label: 'Nettoyage' },
    { value: 'degats', label: 'Dégâts' },
    { value: 'peinture', label: 'Peinture' },
    { value: 'plomberie', label: 'Plomberie' },
    { value: 'electricite', label: 'Électricité' },
    { value: 'serrurerie', label: 'Serrurerie' },
    { value: 'vitrerie', label: 'Vitrerie' },
    { value: 'jardin', label: 'Jardin / Espaces verts' },
    { value: 'loyer_impaye', label: 'Loyer impayé' },
    { value: 'factures', label: 'Factures (eau, électricité, etc.)' },
    { value: 'autre', label: 'Autre' },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [depositsRes, propertiesRes, tenantsRes] = await Promise.all([
        securityDepositsAPI.getAll(),
        propertiesAPI.getAll({ transaction_type: 'location' }),
        clientsAPI.getAll({ client_type: 'locataire' })
      ]);
      setDeposits(depositsRes.data);
      setProperties(propertiesRes.data);
      setTenants(tenantsRes.data);
      
      // Fetch signatures for all deposits
      const sigMap = {};
      for (const dep of depositsRes.data) {
        try {
          const sigRes = await signaturesAPI.get('security_deposit', dep.id);
          if (sigRes.data?.length > 0) sigMap[dep.id] = sigRes.data;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('signatures fetch failed for deposit', dep.id, err);
        }
      }
      setSignatures(sigMap);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    if (!formData.property_id || !formData.tenant_id || !formData.amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSubmitting(true);
    try {
      await securityDepositsAPI.create({
        ...formData,
        amount: parseFloat(formData.amount),
        months_covered: parseInt(formData.months_covered),
        deposit_date: new Date(formData.deposit_date).toISOString()
      });
      
      toast.success('Dépôt de garantie enregistré avec succès');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestitute = async () => {
    if (!restituteData.amount) {
      toast.error('Veuillez entrer le montant à restituer');
      return;
    }
    
    setSubmitting(true);
    try {
      await securityDepositsAPI.restitute(
        selectedDeposit.id, 
        parseFloat(restituteData.amount),
        restituteData.notes
      );
      
      toast.success('Dépôt de garantie restitué avec succès');
      setShowRestituteModal(false);
      setSelectedDeposit(null);
      setRestituteData({ amount: '', notes: '' });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la restitution';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (deposit) => {
    setEditingDeposit(deposit);
    setEditForm({
      property_id: deposit.property_id || '',
      tenant_id: deposit.tenant_id || '',
      amount: deposit.amount?.toString() || '',
      currency: deposit.currency || 'XOF',
      payment_method: deposit.payment_method || 'especes',
      deposit_date: deposit.deposit_date ? deposit.deposit_date.split('T')[0] : '',
      months_covered: deposit.months_covered?.toString() || '2',
      notes: deposit.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingDeposit) return;
    if (!editForm.amount || parseFloat(editForm.amount) <= 0) {
      toast.error('Le montant doit être supérieur à 0');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        property_id: editForm.property_id,
        tenant_id: editForm.tenant_id,
        amount: parseFloat(editForm.amount),
        currency: editForm.currency,
        payment_method: editForm.payment_method,
        months_covered: parseInt(editForm.months_covered, 10),
        notes: editForm.notes || null,
      };
      if (editForm.deposit_date) {
        payload.deposit_date = new Date(editForm.deposit_date).toISOString();
      }
      await securityDepositsAPI.update(editingDeposit.id, payload);
      toast.success('Dépôt de garantie modifié');
      setShowEditModal(false);
      setEditingDeposit(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map(d => d.msg).join(', ')
        : (detail || 'Erreur lors de la modification');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (deposit) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le dépôt ${deposit.reference} ?`)) {
      return;
    }
    
    try {
      await securityDepositsAPI.delete(deposit.id);
      toast.success('Dépôt de garantie supprimé');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
    }
  };

  const handleDownloadPdf = async (deposit) => {
    setDownloadingPdf(deposit.id);
    try {
      const response = await securityDepositsAPI.downloadPdf(deposit.id);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attestation_depot_${deposit.reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Attestation téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleSignDeposit = async (signatureData) => {
    if (!signDeposit) return;
    try {
      await signaturesAPI.save({
        document_type: 'security_deposit',
        document_id: signDeposit.id,
        signature_data: signatureData
      });
      toast.success('Document signé avec succès');
      setShowSignModal(false);
      setSignDeposit(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const getSignatureStatus = (depositId) => {
    const sigs = signatures[depositId] || [];
    const hasTenant = sigs.some(s => s.signer_type === 'tenant');
    const hasAdmin = sigs.some(s => s.signer_type === 'admin' || s.signer_type === 'agent');
    return { hasTenant, hasAdmin, count: sigs.length };
  };

  const resetForm = () => {
    setFormData({
      property_id: '',
      tenant_id: '',
      amount: '',
      currency: 'XOF',
      payment_method: 'especes',
      deposit_date: new Date().toISOString().split('T')[0],
      months_covered: '2',
      notes: ''
    });
  };

  const openRestituteModal = (deposit) => {
    setSelectedDeposit(deposit);
    setRestituteData({
      amount: deposit.amount.toString(),
      notes: ''
    });
    setShowRestituteModal(true);
  };

  // ============== LEASE-END INVOICING ==============

  const openLeaseEndModal = (deposit) => {
    setLeaseEndDeposit(deposit);
    setLeaseEndForm({
      departure_date: new Date().toISOString().split('T')[0],
      notes: '',
      items: [_newItem()],
    });
    setShowLeaseEndModal(true);
  };

  const addLeaseEndItem = () => {
    setLeaseEndForm(prev => ({
      ...prev,
      items: [...prev.items, _newItem()],
    }));
  };

  const removeLeaseEndItem = (index) => {
    setLeaseEndForm(prev => ({
      ...prev,
      items: prev.items.length > 1
        ? prev.items.filter((_, i) => i !== index)
        : prev.items,
    }));
  };

  const updateLeaseEndItem = (index, field, value) => {
    setLeaseEndForm(prev => ({
      ...prev,
      items: prev.items.map((it, i) => (i === index ? { ...it, [field]: value } : it)),
    }));
  };

  const leaseEndTotal = useMemo(() => {
    return leaseEndForm.items.reduce(
      (sum, it) => sum + (parseFloat(it.amount) || 0),
      0
    );
  }, [leaseEndForm.items]);

  const leaseEndSummary = useMemo(() => {
    const depositAmount = leaseEndDeposit?.amount || 0;
    const total = leaseEndTotal;
    const deductible = Math.min(total, depositAmount);
    const refund = Math.max(0, depositAmount - total);
    const collect = Math.max(0, total - depositAmount);
    return { depositAmount, total, deductible, refund, collect };
  }, [leaseEndTotal, leaseEndDeposit]);

  const handleCreateLeaseEndInvoice = async (validateAfter = false) => {
    if (!leaseEndDeposit) return;
    const cleanItems = leaseEndForm.items
      .filter(it => it.description.trim() && parseFloat(it.amount) > 0)
      .map(it => ({
        description: it.description.trim(),
        category: it.category,
        amount: parseFloat(it.amount),
      }));
    if (cleanItems.length === 0) {
      toast.error('Ajoutez au moins un poste valide (description + montant)');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        tenant_id: leaseEndDeposit.tenant_id,
        property_id: leaseEndDeposit.property_id,
        security_deposit_id: leaseEndDeposit.id,
        items: cleanItems,
        departure_date: leaseEndForm.departure_date
          ? new Date(leaseEndForm.departure_date).toISOString()
          : null,
        notes: leaseEndForm.notes || null,
      };
      const res = await leaseEndInvoicesAPI.create(payload);
      const invoice = res.data;
      if (validateAfter && invoice?.id) {
        await leaseEndInvoicesAPI.validate(invoice.id);
        toast.success('Facture validée. Le dépôt a été déduit automatiquement.');
      } else {
        toast.success('Brouillon de facture créé');
      }
      // Download PDF immediately
      if (invoice?.id) {
        try {
          const pdf = await leaseEndInvoicesAPI.downloadPdf(invoice.id);
          const url = window.URL.createObjectURL(new Blob([pdf.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `facture_fin_bail_${invoice.invoice_number}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('lease-end PDF download failed', err);
        }
      }
      setShowLeaseEndModal(false);
      setLeaseEndDeposit(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Erreur lors de la création');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const openLeaseEndList = async (deposit) => {
    setLeaseEndDeposit(deposit);
    setShowLeaseEndListModal(true);
    setLoadingInvoices(true);
    try {
      const res = await leaseEndInvoicesAPI.getAll({ tenant_id: deposit.tenant_id });
      const all = res.data || [];
      const filtered = all.filter(inv => inv.security_deposit_id === deposit.id || !inv.security_deposit_id);
      setLeaseEndInvoicesForDeposit(filtered);
    } catch (e) {
      toast.error('Erreur lors du chargement des factures');
      setLeaseEndInvoicesForDeposit([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleValidateExistingInvoice = async (invoiceId) => {
    try {
      await leaseEndInvoicesAPI.validate(invoiceId);
      toast.success('Facture validée. Dépôt déduit.');
      setShowLeaseEndListModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleDownloadInvoicePdf = async (invoice) => {
    try {
      const pdf = await leaseEndInvoicesAPI.downloadPdf(invoice.id);
      const url = window.URL.createObjectURL(new Blob([pdf.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_fin_bail_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Supprimer cette facture brouillon ?')) return;
    try {
      await leaseEndInvoicesAPI.delete(invoiceId);
      toast.success('Facture supprimée');
      setLeaseEndInvoicesForDeposit(prev => prev.filter(i => i.id !== invoiceId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Filter deposits
  const filteredDeposits = useMemo(() => {
    return deposits.filter(deposit => {
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchTenant = deposit.tenant_name?.toLowerCase().includes(search);
        const matchProperty = deposit.property_title?.toLowerCase().includes(search);
        const matchRef = deposit.reference?.toLowerCase().includes(search);
        if (!matchTenant && !matchProperty && !matchRef) return false;
      }
      
      if (filterStatus !== 'all' && deposit.status !== filterStatus) return false;
      
      return true;
    });
  }, [deposits, searchTerm, filterStatus]);

  // Stats
  const stats = useMemo(() => {
    const total = deposits.length;
    const active = deposits.filter(d => d.status === 'actif').length;
    const returned = deposits.filter(d => d.status === 'restitue').length;
    const totalAmount = deposits
      .filter(d => d.status === 'actif')
      .reduce((sum, d) => sum + (d.amount || 0), 0);
    
    return { total, active, returned, totalAmount };
  }, [deposits]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'actif':
        return <Badge className="bg-green-100 text-green-700">Actif</Badge>;
      case 'restitue':
        return <Badge className="bg-blue-100 text-blue-700">Restitué</Badge>;
      case 'retenu':
        return <Badge className="bg-orange-100 text-orange-700">Retenu</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const paymentMethodLabels = {
    'especes': 'Espèces',
    'virement': 'Virement bancaire',
    'cheque': 'Chèque',
    'mobile_money': 'Mobile Money'
  };

  // Get tenants for selected property
  const availableTenants = useMemo(() => {
    if (!formData.property_id) return tenants;
    return tenants.filter(t => t.property_id === formData.property_id || !t.property_id);
  }, [tenants, formData.property_id]);

  return (
    <div className="space-y-6" data-testid="security-deposits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Dépôts de Garantie
          </h1>
          <p className="text-gray-500 mt-1">
            Gérez les dépôts de garantie des locataires
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#1B3A4B] hover:bg-[#142D3A]"
          data-testid="add-deposit-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Dépôt
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-xl font-semibold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Actifs</p>
                <p className="text-xl font-semibold text-green-600">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <RotateCcw className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Restitués</p>
                <p className="text-xl font-semibold text-blue-600">{stats.returned}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#1B3A4B]/10 rounded-lg">
                <Banknote className="w-4 h-4 text-[#1B3A4B]" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Montant Actif</p>
                <p className="text-lg font-semibold text-[#1B3A4B]">
                  {formatCurrency(stats.totalAmount, 'XOF')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par locataire, bien ou référence..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="actif">Actif</SelectItem>
                <SelectItem value="restitue">Restitué</SelectItem>
                <SelectItem value="retenu">Retenu</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Deposits Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Liste des Dépôts ({filteredDeposits.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" />
            </div>
          ) : filteredDeposits.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucun dépôt de garantie trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Référence</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Locataire</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Bien</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Montant</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Statut</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeposits.map((deposit) => (
                    <tr key={deposit.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-mono text-sm text-[#1B3A4B]">
                        {deposit.reference}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{deposit.tenant_name || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span>{deposit.property_title || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {formatDate(deposit.deposit_date)}
                      </td>
                      <td className="p-3 text-right font-semibold">
                        {formatCurrency(deposit.amount, deposit.currency)}
                        <p className="text-xs text-gray-500">{deposit.months_covered} mois</p>
                      </td>
                      <td className="p-3 text-center">
                        {getStatusBadge(deposit.status)}
                        {(() => {
                          const sigStatus = getSignatureStatus(deposit.id);
                          if (sigStatus.count > 0) {
                            return (
                              <div className="mt-1 flex items-center justify-center gap-1">
                                <PenLine className="w-3 h-3 text-green-600" />
                                <span className="text-xs text-green-600">Signé</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadPdf(deposit)}
                            disabled={downloadingPdf === deposit.id}
                            className="border-[#1B3A4B] text-[#1B3A4B] hover:bg-[#1B3A4B]/10"
                          >
                            {downloadingPdf === deposit.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <Download className="w-3 h-3 mr-1" />
                                PDF
                              </>
                            )}
                          </Button>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" data-testid={`deposit-actions-${deposit.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => openEditModal(deposit)}
                                className="text-[#1B3A4B]"
                                data-testid={`edit-deposit-${deposit.id}`}
                              >
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => { setSignDeposit(deposit); setShowSignModal(true); }}
                                className="text-[#1B3A4B]"
                                data-testid={`sign-deposit-${deposit.id}`}
                              >
                                <PenLine className="w-4 h-4 mr-2" />
                                Signer le document
                              </DropdownMenuItem>
                              {deposit.status === 'actif' && (
                                <DropdownMenuItem
                                  onClick={() => openLeaseEndModal(deposit)}
                                  className="text-amber-700"
                                  data-testid={`lease-end-invoice-${deposit.id}`}
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  Facturer fin de bail
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => openLeaseEndList(deposit)}
                                className="text-[#1B3A4B]"
                                data-testid={`view-lease-end-invoices-${deposit.id}`}
                              >
                                <ListChecks className="w-4 h-4 mr-2" />
                                Factures fin de bail
                              </DropdownMenuItem>
                              {deposit.status === 'actif' && (
                                <DropdownMenuItem
                                  onClick={() => openRestituteModal(deposit)}
                                  className="text-blue-600"
                                >
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Restituer le dépôt
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDelete(deposit)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1B3A4B]" />
              Nouveau Dépôt de Garantie
            </DialogTitle>
            <DialogDescription>
              Enregistrez un nouveau dépôt de garantie pour un locataire
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Bien immobilier *</Label>
              <Select
                value={formData.property_id}
                onValueChange={(value) => setFormData({...formData, property_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un bien" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(prop => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.title} ({prop.property_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Locataire *</Label>
              <Select
                value={formData.tenant_id}
                onValueChange={(value) => setFormData({...formData, tenant_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un locataire" />
                </SelectTrigger>
                <SelectContent>
                  {availableTenants.map(tenant => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name} ({tenant.client_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant *</Label>
                <Input
                  type="number"
                  placeholder="Ex: 100000"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Mois couverts</Label>
                <Select
                  value={formData.months_covered}
                  onValueChange={(value) => setFormData({...formData, months_covered: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 mois</SelectItem>
                    <SelectItem value="2">2 mois</SelectItem>
                    <SelectItem value="3">3 mois</SelectItem>
                    <SelectItem value="4">4 mois</SelectItem>
                    <SelectItem value="6">6 mois</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de versement</Label>
                <Input
                  type="date"
                  value={formData.deposit_date}
                  onChange={(e) => setFormData({...formData, deposit_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Mode de paiement</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({...formData, payment_method: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="especes">Espèces</SelectItem>
                    <SelectItem value="virement">Virement bancaire</SelectItem>
                    <SelectItem value="cheque">Chèque</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea
                placeholder="Observations..."
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={submitting}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={submitting}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restitute Modal */}
      <Dialog open={showRestituteModal} onOpenChange={setShowRestituteModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-blue-600" />
              Restituer le Dépôt
            </DialogTitle>
            <DialogDescription>
              Restituer le dépôt de garantie au locataire
            </DialogDescription>
          </DialogHeader>
          
          {selectedDeposit && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Dépôt original</p>
                <p className="font-semibold text-lg">
                  {formatCurrency(selectedDeposit.amount, selectedDeposit.currency)}
                </p>
                <p className="text-sm text-gray-500">{selectedDeposit.tenant_name}</p>
              </div>
              
              <div>
                <Label>Montant à restituer *</Label>
                <Input
                  type="number"
                  placeholder="Montant"
                  value={restituteData.amount}
                  onChange={(e) => setRestituteData({...restituteData, amount: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Déduisez les éventuels frais ou impayés
                </p>
              </div>
              
              <div>
                <Label>Motif / Notes</Label>
                <Textarea
                  placeholder="Ex: Restitution totale, fin de bail..."
                  value={restituteData.notes}
                  onChange={(e) => setRestituteData({...restituteData, notes: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestituteModal(false)} disabled={submitting}>
              Annuler
            </Button>
            <Button 
              onClick={handleRestitute} 
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Restituer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal (admin only) */}
      <Dialog open={showEditModal} onOpenChange={(open) => { setShowEditModal(open); if (!open) setEditingDeposit(null); }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#D4A745]" />
              Modifier le Dépôt de Garantie
            </DialogTitle>
            <DialogDescription>
              {editingDeposit && `Référence : ${editingDeposit.reference}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Bien immobilier</Label>
              <Select
                value={editForm.property_id}
                onValueChange={(value) => setEditForm({ ...editForm, property_id: value })}
              >
                <SelectTrigger data-testid="edit-deposit-property">
                  <SelectValue placeholder="Sélectionner un bien" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.title} ({prop.property_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Locataire</Label>
              <Select
                value={editForm.tenant_id}
                onValueChange={(value) => setEditForm({ ...editForm, tenant_id: value })}
              >
                <SelectTrigger data-testid="edit-deposit-tenant">
                  <SelectValue placeholder="Sélectionner un locataire" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name} ({tenant.client_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant *</Label>
                <Input
                  type="number"
                  placeholder="Ex: 100000"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  data-testid="edit-deposit-amount"
                />
              </div>
              <div>
                <Label>Mois couverts</Label>
                <Select
                  value={editForm.months_covered}
                  onValueChange={(value) => setEditForm({ ...editForm, months_covered: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 mois</SelectItem>
                    <SelectItem value="2">2 mois</SelectItem>
                    <SelectItem value="3">3 mois</SelectItem>
                    <SelectItem value="4">4 mois</SelectItem>
                    <SelectItem value="6">6 mois</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de versement</Label>
                <Input
                  type="date"
                  value={editForm.deposit_date}
                  onChange={(e) => setEditForm({ ...editForm, deposit_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Mode de paiement</Label>
                <Select
                  value={editForm.payment_method}
                  onValueChange={(value) => setEditForm({ ...editForm, payment_method: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="especes">Espèces</SelectItem>
                    <SelectItem value="virement">Virement bancaire</SelectItem>
                    <SelectItem value="cheque">Chèque</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Observations..."
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditModal(false); setEditingDeposit(null); }} disabled={submitting}>
              Annuler
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={submitting}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              data-testid="save-edit-deposit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer les modifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Modal */}
      <Dialog open={showSignModal} onOpenChange={(open) => { setShowSignModal(open); if (!open) setSignDeposit(null); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-[#1B3A4B]" />
              Signer le Dépôt de Garantie
            </DialogTitle>
            <DialogDescription>
              {signDeposit && `${signDeposit.reference} - ${signDeposit.tenant_name}`}
            </DialogDescription>
          </DialogHeader>
          <SignaturePad
            onSave={handleSignDeposit}
            onCancel={() => { setShowSignModal(false); setSignDeposit(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Lease-End Invoice Modal */}
      <Dialog
        open={showLeaseEndModal}
        onOpenChange={(open) => { setShowLeaseEndModal(open); if (!open) setLeaseEndDeposit(null); }}
      >
        <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-700" />
              Facturation de fin de bail
            </DialogTitle>
            <DialogDescription>
              {leaseEndDeposit && (
                <>
                  {leaseEndDeposit.tenant_name} — {leaseEndDeposit.property_title} (Dépôt :{' '}
                  {formatCurrency(leaseEndDeposit.amount, leaseEndDeposit.currency)})
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Date de départ</Label>
                <Input
                  type="date"
                  value={leaseEndForm.departure_date}
                  onChange={(e) => setLeaseEndForm({ ...leaseEndForm, departure_date: e.target.value })}
                  data-testid="lease-end-departure-date"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addLeaseEndItem}
                  className="w-full"
                  data-testid="add-lease-end-item-btn"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Ajouter un poste
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Postes facturés</Label>
              {leaseEndForm.items.map((item, idx) => (
                <div
                  key={item._id || `item-${idx}`}
                  className="grid grid-cols-12 gap-2 items-start p-2 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <div className="col-span-12 md:col-span-5">
                    <Input
                      placeholder="Description (ex: peinture salon)"
                      value={item.description}
                      onChange={(e) => updateLeaseEndItem(idx, 'description', e.target.value)}
                      data-testid={`lease-end-item-desc-${idx}`}
                    />
                  </div>
                  <div className="col-span-7 md:col-span-4">
                    <Select
                      value={item.category}
                      onValueChange={(v) => updateLeaseEndItem(idx, 'category', v)}
                    >
                      <SelectTrigger data-testid={`lease-end-item-cat-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEASE_END_CATEGORIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Input
                      type="number"
                      placeholder="Montant"
                      value={item.amount}
                      onChange={(e) => updateLeaseEndItem(idx, 'amount', e.target.value)}
                      data-testid={`lease-end-item-amount-${idx}`}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLeaseEndItem(idx)}
                      disabled={leaseEndForm.items.length === 1}
                      data-testid={`remove-lease-end-item-${idx}`}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea
                placeholder="Ex: état des lieux du 12/06/2026..."
                value={leaseEndForm.notes}
                onChange={(e) => setLeaseEndForm({ ...leaseEndForm, notes: e.target.value })}
                rows={2}
                data-testid="lease-end-notes"
              />
            </div>

            {/* Summary */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Dépôt de garantie</span>
                <span className="font-semibold">
                  {formatCurrency(leaseEndSummary.depositAmount, leaseEndDeposit?.currency || 'XOF')}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total facture</span>
                <span className="font-semibold text-red-600">
                  - {formatCurrency(leaseEndSummary.total, leaseEndDeposit?.currency || 'XOF')}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Déduit du dépôt</span>
                <span className="font-semibold">
                  {formatCurrency(leaseEndSummary.deductible, leaseEndDeposit?.currency || 'XOF')}
                </span>
              </div>
              <div className="border-t border-amber-300 pt-2 flex justify-between">
                {leaseEndSummary.refund > 0 ? (
                  <>
                    <span className="font-semibold text-green-700">À rembourser au locataire</span>
                    <span className="font-bold text-green-700 text-lg" data-testid="lease-end-refund">
                      {formatCurrency(leaseEndSummary.refund, leaseEndDeposit?.currency || 'XOF')}
                    </span>
                  </>
                ) : leaseEndSummary.collect > 0 ? (
                  <>
                    <span className="font-semibold text-red-700">À percevoir du locataire</span>
                    <span className="font-bold text-red-700 text-lg" data-testid="lease-end-collect">
                      {formatCurrency(leaseEndSummary.collect, leaseEndDeposit?.currency || 'XOF')}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-gray-700">Solde nul</span>
                    <span className="font-bold text-gray-700 text-lg">0</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowLeaseEndModal(false)}
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button
              variant="outline"
              onClick={() => handleCreateLeaseEndInvoice(false)}
              disabled={submitting}
              data-testid="save-lease-end-draft-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              Enregistrer en brouillon
            </Button>
            <Button
              onClick={() => handleCreateLeaseEndInvoice(true)}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="validate-lease-end-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowDownToLine className="w-4 h-4 mr-2" />}
              Valider et déduire du dépôt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lease-End Invoices List Modal */}
      <Dialog
        open={showLeaseEndListModal}
        onOpenChange={(open) => { setShowLeaseEndListModal(open); if (!open) setLeaseEndInvoicesForDeposit([]); }}
      >
        <DialogContent className="sm:max-w-[760px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-[#1B3A4B]" />
              Factures de fin de bail
            </DialogTitle>
            <DialogDescription>
              {leaseEndDeposit && (
                <>{leaseEndDeposit.tenant_name} — {leaseEndDeposit.property_title}</>
              )}
            </DialogDescription>
          </DialogHeader>

          {loadingInvoices ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : leaseEndInvoicesForDeposit.length === 0 ? (
            <p className="text-center text-gray-500 py-6">Aucune facture pour ce locataire.</p>
          ) : (
            <div className="space-y-2 py-2">
              {leaseEndInvoicesForDeposit.map((inv) => (
                <div key={inv.id} className="border border-gray-200 rounded-lg p-3 bg-white" data-testid={`invoice-row-${inv.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="font-semibold text-[#1B3A4B]">{inv.invoice_number}</div>
                      <div className="text-xs text-gray-500">
                        {inv.departure_date ? `Départ : ${formatDate(inv.departure_date)}` : ''} ·{' '}
                        {inv.items?.length || 0} poste(s)
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge className={inv.status === 'validee' ? 'bg-green-600' : 'bg-gray-400'}>
                          {inv.status === 'validee' ? 'Validée' : 'Brouillon'}
                        </Badge>
                        <span className="text-sm font-semibold">
                          {formatCurrency(inv.total_amount, inv.currency)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadInvoicePdf(inv)}
                        title="Télécharger PDF"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {inv.status !== 'validee' && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleValidateExistingInvoice(inv.id)}
                            title="Valider"
                            className="text-green-700"
                            data-testid={`validate-invoice-${inv.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteInvoice(inv.id)}
                            title="Supprimer"
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaseEndListModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityDeposits;
